/**
 * Suppression Rules API Service
 *
 * Service functions for interacting with the suppression rules API endpoints.
 */

import { apiClient } from "./api";
import type { SuppressionRule } from "@/types/suppressionRule";

const API_PREFIX = "/api/v1/suppression-rules";

/**
 * Paginated response from backend
 */
export interface PaginatedSuppressionRulesResponse {
  data: SuppressionRule[];
  total_count: number;
  has_more: boolean;
  page: number;
  items_per_page: number;
}

/**
 * Request body for creating a suppression rule
 */
export interface CreateSuppressionRuleData {
  name: string;
  description?: string | null;
  match_issue_type?: string | null;
  match_title_pattern?: string | null;
  match_severity?: string | null;
  match_scanner?: string | null;
  expires_at?: string | null;
  reason?: string | null;
  source_issue_id?: number | null;
}

/**
 * Request body for updating a suppression rule
 */
export interface UpdateSuppressionRuleData {
  name?: string;
  description?: string | null;
  match_issue_type?: string | null;
  match_title_pattern?: string | null;
  match_severity?: string | null;
  match_scanner?: string | null;
  is_active?: boolean;
  reason?: string | null;
}

/**
 * Response from create/update endpoints that includes retroactive suppression count.
 */
export interface SuppressionRuleCreateResponse {
  rule: SuppressionRule;
  suppressed_count: number;
}

/**
 * Create a new suppression rule.
 * @param data - The suppression rule data
 */
export async function createSuppressionRule(
  data: CreateSuppressionRuleData
): Promise<SuppressionRuleCreateResponse> {
  return apiClient.post<SuppressionRuleCreateResponse>(API_PREFIX + "/", data);
}

/**
 * List suppression rules for the current organization.
 * @param page - The page number (default: 1)
 * @param isActive - Optional filter by active status
 */
export async function listSuppressionRules(
  page: number = 1,
  isActive?: boolean,
  itemsPerPage?: number
): Promise<PaginatedSuppressionRulesResponse> {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  if (isActive !== undefined) {
    params.append("is_active", isActive.toString());
  }
  if (itemsPerPage !== undefined) {
    params.append("items_per_page", itemsPerPage.toString());
  }
  return apiClient.get<PaginatedSuppressionRulesResponse>(
    `${API_PREFIX}/?${params.toString()}`
  );
}

/**
 * Update a suppression rule.
 * @param ruleId - The numeric ID of the rule
 * @param data - The update data
 */
export async function updateSuppressionRule(
  ruleId: number,
  data: UpdateSuppressionRuleData
): Promise<SuppressionRuleCreateResponse> {
  return apiClient.patch<SuppressionRuleCreateResponse>(`${API_PREFIX}/${ruleId}`, data);
}

/**
 * Delete (soft-delete) a suppression rule.
 * @param ruleId - The numeric ID of the rule
 */
export async function deleteSuppressionRule(
  ruleId: number
): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`${API_PREFIX}/${ruleId}`);
}

export interface SuppressionRulePreviewRequest {
  match_issue_type?: string | null;
  match_title_pattern?: string | null;
  match_severity?: string | null;
  match_scanner?: string | null;
}

export interface SuppressionRulePreviewIssue {
  id: number;
  title: string;
  severity: string;
  issue_type: string;
}

export interface SuppressionRulePreviewResponse {
  total_count: number;
  sample_issues: SuppressionRulePreviewIssue[];
}

export async function previewSuppressionRule(
  data: SuppressionRulePreviewRequest
): Promise<SuppressionRulePreviewResponse> {
  return apiClient.post<SuppressionRulePreviewResponse>(
    `${API_PREFIX}/preview`,
    data
  );
}
